import { useState, useEffect } from "react";
import { YStack, XStack, Text, Stack } from "tamagui";
import { useUser, Address } from "@utils/store/user/useUser";
import { useOrder } from "@utils/store/cart/useOrder";
import { appToast } from "@components/Toast";
import type { AddAddressFormData } from "@components/address/AddAddressFlowSheet";
import KioskAddressSheet from "./KioskAddressSheet";
import { createCustomerAddress } from "@screen/QuickOrder/utils/mockApi";
import { combinedAddress } from "@components/address/func/addressTool";
import { findDuplicateAddress } from "@utils/func/addressDedup";
import { kiosk } from "../kioskTheme";
import KioskIcon from "../KioskIcon";

/**
 * 送達地址底部選單（home / 確認頁共用）。
 * - 切換：地址清單來自 chooseIdentity.address；選取更新 choose_address。
 * - 新增：走與註冊/快速訂單相同的 AddAddressFlowSheet（搜尋 + 地圖）→ createCustomerAddress 存到帳戶。
 * 注意：已建立的草稿綁定原 customer_address_id，下單前切換才會生效。
 */
const AddressPickerSheet = ({
  isOpen,
  onClose,
  startInAdd,
  autoLocate,
  initial,
  confirmTitle,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** 開啟時直接進入「新增地址」表單（無既有地址時用，例如 QR 進 kiosk 的允許定位入口）。 */
  startInAdd?: boolean;
  /** 傳給新增地址表單：開啟即自動 GPS 定位帶入。 */
  autoLocate?: boolean;
  /** 預填新增表單欄位（給「確認送達地址」用，讓客戶直接修改定位帶入的地址）。 */
  initial?: { city?: string; area?: string; road?: string; streetNumber?: string; floor?: string; room?: string };
  /** 新增表單自訂標題（例：確認送達地址）。 */
  confirmTitle?: string;
}) => {
  const { chooseIdentity, setChooseIdentity } = useUser();
  const { deliveryAreas } = useOrder();
  const [showAdd, setShowAdd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // startInAdd：開啟時略過地址清單，直接開新增表單（沒有既有地址的情境）。
  useEffect(() => {
    if (isOpen && startInAdd) setShowAdd(true);
    if (!isOpen) setShowAdd(false);
  }, [isOpen, startInAdd]);

  // chooseIdentity.address 型別為 Address[] | string，這裡只取陣列形式。
  // 過濾沒有內容的地址（佔位 CIS 代表地址 full_address 可能為空）→ 不渲染空白卡
  const list: Address[] = (Array.isArray(chooseIdentity?.address) ? (chooseIdentity!.address as Address[]) : []).filter(
    (a) => !!a.full_address?.trim(),
  );
  const current = chooseIdentity?.choose_address;
  // 僅 owner 可新增地址（後端 POST /delivery/address 限 owner，member 會 403）
  const canAdd = chooseIdentity?.role === "owner";

  const pick = (addr: Address) => {
    if (chooseIdentity) setChooseIdentity({ ...chooseIdentity, choose_address: addr });
    onClose();
  };

  // 新增地址：表單送出 → 建立客戶地址 → 加入清單並設為目前選取
  const handleAdd = async (data: AddAddressFormData) => {
    if (submitting) return;
    const display = combinedAddress({
      city: data.city || "",
      area: data.area || "",
      road: data.road || "",
      streetNumber: data.streetNumber,
      floor: data.floor,
      room: data.room,
    });
    const fullAddress = data.otherAddress?.trim() ? `${display} ${data.otherAddress}` : display;

    // 重複地址：清單已有相同 full_address → 不新增，提醒並直接選用既有那筆
    const duplicate = findDuplicateAddress(list, fullAddress);
    if (duplicate) {
      if (chooseIdentity) setChooseIdentity({ ...chooseIdentity, choose_address: duplicate });
      setShowAdd(false);
      appToast.error("此地址已存在，已為您選用");
      onClose();
      return;
    }

    setSubmitting(true);
    try {
      const res = await createCustomerAddress({
        addressInfos: {
          city: data.city || undefined,
          site_id: data.area || undefined,
          road: data.road || undefined,
          address_number: data.streetNumber?.trim() || undefined,
          floor: data.floor || undefined,
          room: data.room || undefined,
          extra: data.otherAddress || undefined,
          latitude: data.latitude ?? undefined,
          longitude: data.longitude ?? undefined,
        },
      });
      const newId = res.customerAddressResult.customer_address_id;
      const newAddr: Address = {
        customer_address_id: newId,
        full_address: fullAddress,
        city: data.city,
        district: data.area,
        floor: data.floor,
      };
      if (chooseIdentity) {
        setChooseIdentity({ ...chooseIdentity, address: [...list, newAddr], choose_address: newAddr });
      }
      setShowAdd(false);
      appToast.success("已新增並選用此地址");
      onClose();
    } catch (e) {
      console.error("createCustomerAddress error", e);
      appToast.error("新增地址失敗，請稍後再試");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Stack
        position="absolute"
        t={0}
        l={0}
        r={0}
        b={0}
        backgroundColor="rgba(17,32,58,.45)"
        jc="flex-end"
        style={{ zIndex: 100 }}
        onPress={onClose}>
        <YStack
          className="kiosk-sheetin kiosk-noscroll"
          backgroundColor="#fff"
          borderTopLeftRadius={24}
          borderTopRightRadius={24}
          px={18}
          pt={8}
          pb={24}
          maxHeight="85%"
          style={{ overflowY: "auto" }}
          onPress={(e: any) => e.stopPropagation?.()}>
          <Stack alignSelf="center" width={40} height={5} borderRadius={3} backgroundColor="#dfe3ec" my={10} />
          <Text fontSize={20} fontWeight="800" letterSpacing={-0.4} mb={4} color={kiosk.ink}>
            選擇送達地址
          </Text>
          <Text fontSize={13} color={kiosk.grey} mb={16}>
            不同區域配送價格可能不同
          </Text>
          {list.length === 0 && (
            <Text fontSize={13} color={kiosk.grey} py={20} textAlign="center">
              尚無地址，點下方「新增地址」
            </Text>
          )}
          {list.map((o) => {
            const selected = current?.customer_address_id === o.customer_address_id;
            return (
              <XStack
                key={String(o.customer_address_id)}
                ai="center"
                gap={11}
                borderWidth={2}
                borderColor={selected ? kiosk.blue : kiosk.line}
                backgroundColor={selected ? kiosk.pillBg : "#fff"}
                borderRadius={14}
                p={14}
                mb={10}
                cursor="pointer"
                onPress={() => pick(o)}
                pressStyle={{ scale: 0.98 }}>
                <Stack>
                  <KioskIcon name="pin" size={20} color="#e0566f" />
                </Stack>
                <Text flex={1} fontSize={14} fontWeight="700" color={kiosk.ink} lineHeight={20}>
                  {o.full_address}
                </Text>
                {selected && <KioskIcon name="check" size={18} color={kiosk.blue} strokeWidth={2.4} />}
              </XStack>
            );
          })}
          {canAdd ? (
            <XStack
              ai="center"
              jc="center"
              borderWidth={2}
              borderColor={kiosk.line}
              borderRadius={14}
              p={14}
              cursor="pointer"
              onPress={() => setShowAdd(true)}
              pressStyle={{ opacity: 0.7 }}
              style={{ borderStyle: "dashed" }}>
              <Text color={kiosk.blue} fontSize={14} fontWeight="700">
                ＋ 新增地址
              </Text>
            </XStack>
          ) : (
            <Text fontSize={12} color={kiosk.grey} textAlign="center" py={10}>
              僅帳戶擁有者可新增地址，請聯絡擁有者
            </Text>
          )}
        </YStack>
      </Stack>

      <KioskAddressSheet isOpen={showAdd} onClose={() => setShowAdd(false)} onSubmit={handleAdd} deliveryAreas={deliveryAreas} autoLocate={autoLocate} initial={initial} title={confirmTitle} />
    </>
  );
};

export default AddressPickerSheet;
